import React, { useState, useEffect } from 'react';
import axios_instance from '../api/axios_instance';
import { Loader2, Package, Trash2, PlusCircle, Image as ImageIcon, Languages, CheckCircle2, Star, Flame, Sparkles, AlertCircle, X } from 'lucide-react';

const AddProduct = () => {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [variants, setVariants] = useState([{ grammage: '', price: '', discountPrice: '', isOnSale: false, stock: 0 }]);
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  
  // Custom Notification State
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  const [formData, setFormData] = useState({
    nameAr: '', nameEn: '', nameFr: '',
    descAr: '', descEn: '', descFr: '',
    detailedAr: '', detailedEn: '', detailedFr: '',
    category: [],
    subCategory: [],
    isNewProduct: false,
    isMostSold: false,
    featured: false,
    isPack: false,
    status: 'available'
  });

  const inputStyle = "w-full p-2.5 border border-gray-300 rounded-lg outline-none bg-white text-sm text-gray-900 focus:border-emerald-500 transition-colors placeholder:text-gray-400";
  const labelStyle = "text-[11px] font-black text-gray-500 mb-1.5 block uppercase tracking-wider";

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await axios_instance.get('/api/categories');
        setCategories(res.data);
      } catch (err) { console.error(err); }
    };
    fetchCats();
  }, []);

  useEffect(() => {
    const filtered = categories.filter(c => 
      c.parentCategory && formData.category.includes(c.parentCategory._id)
    );
    setSubCategories(filtered);
    const validSubIds = filtered.map(f => f._id);
    setFormData(prev => ({
      ...prev,
      subCategory: prev.subCategory.filter(id => validSubIds.includes(id))
    }));
  }, [formData.category, categories]);

  const showToast = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 5000);
  };

  const resetForm = () => {
    setFormData({
      nameAr: '', nameEn: '', nameFr: '',
      descAr: '', descEn: '', descFr: '',
      detailedAr: '', detailedEn: '', detailedFr: '',
      category: [], subCategory: [],
      isNewProduct: false, isMostSold: false, featured: false, isPack: false, status: 'available'
    });
    setVariants([{ grammage: '', price: '', discountPrice: '', isOnSale: false, stock: 0 }]);
    setImages([]);
    setPreviews([]);
  };

  const toggleArray = (key, id) => {
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].includes(id) ? prev[key].filter(i => i !== id) : [...prev[key], id]
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 4) return showToast("الحد الأقصى 4 صور فقط", "error");
    setImages([...images, ...files]);
    setPreviews([...previews, ...files.map(file => URL.createObjectURL(file))]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const isFormValid = formData.nameAr && formData.nameEn && formData.category.length > 0 && images.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const data = new FormData();
    data.append('name[ar]', formData.nameAr);
    data.append('name[en]', formData.nameEn);
    data.append('name[fr]', formData.nameFr);
    data.append('description[ar]', formData.descAr);
    data.append('description[en]', formData.descEn);
    data.append('description[fr]', formData.descFr);
    data.append('detailedDescription[ar]', formData.detailedAr);
    data.append('detailedDescription[en]', formData.detailedEn);
    data.append('detailedDescription[fr]', formData.detailedFr);

    formData.category.forEach(id => data.append('category[]', id));
    formData.subCategory.forEach(id => data.append('subCategory[]', id));
    
    data.append('isNewProduct', formData.isNewProduct);
    data.append('isMostSold', formData.isMostSold);
    data.append('featured', formData.featured);
    data.append('isPack', formData.isPack);
    data.append('status', formData.status);
    data.append('variants', JSON.stringify(variants));
    images.forEach(img => data.append('images', img));

    try {
      await axios_instance.post('/api/products', data);
      showToast("تمت إضافة المنتج بنجاح إلى المتجر");
      resetForm(); // Success: Clean all fields
    } catch (err) { 
      const errorMsg = err.response?.data?.message || "فشل في إضافة المنتج، يرجى المحاولة مرة أخرى";
      showToast(errorMsg, "error");
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen relative" dir="rtl">
      
      {/* NOTIFICATION TOAST */}
      {notification.show && (
        <div className={`fixed top-10 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 px-6 py-4 rounded-2xl border animate-in fade-in slide-in-from-top-10 duration-300 ${
          notification.type === 'success' ? 'bg-white border-emerald-100 text-emerald-700' : 'bg-white border-red-100 text-red-700'
        }`}>
          {notification.type === 'success' ? <CheckCircle2 size={20} className="text-emerald-500" /> : <AlertCircle size={20} className="text-red-500" />}
          <span className="text-sm font-bold">{notification.message}</span>
          <button onClick={() => setNotification({ ...notification, show: false })} className="mr-4 text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="max-w-6xl mx-auto bg-white border border-gray-200 rounded-2xl p-8 relative">
        
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 pb-5 border-b border-gray-100 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600"><Package size={22} /></div>
            <h2 className="text-xl font-black text-gray-800 tracking-tight">إضافة منتج احترافي</h2>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setFormData({...formData, isNewProduct: !formData.isNewProduct})}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] font-black border transition-all ${formData.isNewProduct ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white text-gray-400 border-gray-200'}`}>
              <Sparkles size={12} /> NEW
            </button>
            <button type="button" onClick={() => setFormData({...formData, isMostSold: !formData.isMostSold})}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] font-black border transition-all ${formData.isMostSold ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white text-gray-400 border-gray-200'}`}>
              <Flame size={12} /> BEST SELLER
            </button>
            <button type="button" onClick={() => setFormData({...formData, featured: !formData.featured})}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] font-black border transition-all ${formData.featured ? 'bg-amber-500 border-amber-500 text-white' : 'bg-white text-gray-400 border-gray-200'}`}>
              <Star size={12} /> FEATURED
            </button>
            <button type="button" onClick={() => setFormData({...formData, isPack: !formData.isPack})}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] font-black border transition-all ${formData.isPack ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white text-gray-400 border-gray-200'}`}>
              <Package size={12} /> PACK
            </button>

            {/* Status selector */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
              {[
                { value: 'available', label: 'متوفر', color: 'bg-emerald-500' },
                { value: 'low_stock', label: 'محدود', color: 'bg-orange-400' },
                { value: 'out_of_stock', label: 'نفذ', color: 'bg-red-500' }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFormData({...formData, status: opt.value})}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black transition-all ${formData.status === opt.value ? `${opt.color} text-white` : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${formData.status === opt.value ? 'bg-white' : opt.color}`} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-12">
          
          {/* NAMES */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className={labelStyle}>الاسم (AR)</label>
              <input type="text" value={formData.nameAr} className={inputStyle} onChange={e => setFormData({...formData, nameAr: e.target.value})} />
            </div>
            <div dir="ltr">
              <label className={labelStyle}>Product Name (EN)</label>
              <input type="text" value={formData.nameEn} className={inputStyle} onChange={e => setFormData({...formData, nameEn: e.target.value})} />
            </div>
            <div dir="ltr">
              <label className={labelStyle}>Nom du Produit (FR)</label>
              <input type="text" value={formData.nameFr} className={inputStyle} onChange={e => setFormData({...formData, nameFr: e.target.value})} />
            </div>
          </div>

          {/* CATEGORIES */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
            <div className="space-y-3">
              <label className={labelStyle}>التصنيفات الرئيسية *</label>
              <div className="flex flex-wrap gap-2">
                {categories.filter(c => !c.parentCategory).map(c => (
                  <button key={c._id} type="button" onClick={() => toggleArray('category', c._id)}
                    className={`px-4 py-2 rounded-lg text-[11px] font-bold border transition-all ${formData.category.includes(c._id) ? 'bg-gray-800 border-gray-800 text-white' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400'}`}>
                    {c.name.ar}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <label className={labelStyle}>التصنيفات الفرعية</label>
              <div className="flex flex-col gap-4 max-h-[250px] overflow-y-auto pr-2">
                {formData.category.length > 0 ? (
                  formData.category.map((mainId) => {
                    const mainCat = categories.find(c => c._id === mainId);
                    const relatedSubs = subCategories.filter(s => s.parentCategory._id === mainId);
                    if (relatedSubs.length === 0) return null;
                    return (
                      <div key={mainId} className="bg-white p-3 rounded-xl border border-gray-100">
                        <p className="text-[9px] font-black text-emerald-600 mb-2 uppercase">{mainCat?.name?.ar}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {relatedSubs.map(s => (
                            <button key={s._id} type="button" onClick={() => toggleArray('subCategory', s._id)}
                              className={`px-3 py-1 rounded-lg text-[10px] font-bold border transition-all ${formData.subCategory.includes(s._id) ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-gray-200 text-gray-400 hover:border-emerald-200'}`}>
                              {s.name.ar}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })
                ) : <p className="text-[10px] text-gray-400 italic">اختر تصنيفاً رئيسياً</p>}
              </div>
            </div>
          </div>

          {/* SHORT DESCRIPTIONS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className={labelStyle}>الوصف المختصر (AR)</label>
              <textarea value={formData.descAr} className={`${inputStyle} h-24 resize-none`} onChange={e => setFormData({...formData, descAr: e.target.value})} />
            </div>
            <div dir="ltr">
              <label className={labelStyle}>Short Description (EN)</label>
              <textarea value={formData.descEn} className={`${inputStyle} h-24 resize-none`} onChange={e => setFormData({...formData, descEn: e.target.value})} />
            </div>
            <div dir="ltr">
              <label className={labelStyle}>Description Courte (FR)</label>
              <textarea value={formData.descFr} className={`${inputStyle} h-24 resize-none`} onChange={e => setFormData({...formData, descFr: e.target.value})} />
            </div>
          </div>

          {/* DETAILED CONTENT */}
          <div className="pt-6 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-6 text-gray-400 uppercase tracking-widest text-[10px] font-black">
              <Languages size={14} /> الوصف التفصيلي (DETAILED CONTENT)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelStyle}>المحتوى التفصيلي (AR)</label>
                <textarea placeholder="التفاصيل الكاملة بالعربية..." value={formData.detailedAr} className={`${inputStyle} h-44`} onChange={e => setFormData({...formData, detailedAr: e.target.value})} />
              </div>
              <div dir="ltr">
                <label className={labelStyle}>Detailed Content (EN)</label>
                <textarea placeholder="Full details in English..." value={formData.detailedEn} className={`${inputStyle} h-44`} onChange={e => setFormData({...formData, detailedEn: e.target.value})} />
              </div>
              <div dir="ltr">
                <label className={labelStyle}>Contenu Détaillé (FR)</label>
                <textarea placeholder="Détails complets en français..." value={formData.detailedFr} className={`${inputStyle} h-44`} onChange={e => setFormData({...formData, detailedFr: e.target.value})} />
              </div>
            </div>
          </div>

          {/* VARIANTS & MEDIA */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 h-fit">
              <div className="flex justify-between items-center mb-4">
                <span className={labelStyle}>الأوزان والأسعار *</span>
                <button type="button" onClick={() => setVariants([...variants, { grammage: '', price: '', discountPrice: '', isOnSale: false, stock: 0 }])} className="text-emerald-600 flex items-center gap-1 text-[11px] font-bold"><PlusCircle size={14} /> إضافة خيار</button>
              </div>
              {variants.map((v, i) => (
                <div key={i} className="mb-4 p-3 bg-white rounded-xl border border-gray-200">
                  <div className="flex gap-2 mb-2">
                    <input type="text" placeholder="الوزن / الحجم" value={v.grammage} className={inputStyle} onChange={e => { let vts = [...variants]; vts[i].grammage = e.target.value; setVariants(vts); }} />
                    <input type="number" placeholder="السعر الأصلي" value={v.price} className={inputStyle} onChange={e => { let vts = [...variants]; vts[i].price = e.target.value; setVariants(vts); }} />
                    {variants.length > 1 && <button type="button" onClick={() => setVariants(variants.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 px-1"><Trash2 size={16} /></button>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { let vts = [...variants]; vts[i].isOnSale = !vts[i].isOnSale; setVariants(vts); }}
                      className={`shrink-0 px-3 py-1.5 rounded-lg text-[10px] font-black border transition-all ${v.isOnSale ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-gray-200 text-gray-400'}`}
                    >
                      % تخفيض
                    </button>
                    {v.isOnSale && (
                      <input
                        type="number"
                        placeholder="السعر بعد التخفيض"
                        value={v.discountPrice}
                        className={inputStyle}
                        onChange={e => { let vts = [...variants]; vts[i].discountPrice = e.target.value; setVariants(vts); }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <label className={labelStyle}>صور المنتج (الحد الأقصى 4)</label>
              <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 bg-white flex flex-col items-center justify-center min-h-[175px] hover:border-emerald-300 transition-all">
                <input type="file" multiple id="file-upload" className="hidden" onChange={handleImageChange} accept="image/*" />
                {previews.length === 0 ? (
                  <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center text-gray-400">
                    <ImageIcon size={36} strokeWidth={1.2} />
                    <span className="text-[10px] font-black mt-3 uppercase tracking-widest">تحميل الصور</span>
                  </label>
                ) : (
                  <div className="grid grid-cols-4 gap-3 w-full">
                    {previews.map((p, i) => (
                      <div key={i} className="relative aspect-square border border-gray-100 rounded-xl overflow-hidden group">
                        <img src={p} className="w-full h-full object-cover" alt="" />
                        <button type="button" onClick={() => removeImage(i)} className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"><Trash2 size={16}/></button>
                      </div>
                    ))}
                    {previews.length < 4 && <label htmlFor="file-upload" className="aspect-square border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center text-gray-300 cursor-pointer"><PlusCircle size={20}/></label>}
                  </div>
                )}
              </div>
            </div>
          </div>

          <button type="submit" disabled={loading || !isFormValid}
            className={`w-full py-4 rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 transition-all ${isFormValid ? 'bg-gray-900 text-white hover:bg-black' : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'}`}>
            {loading ? <Loader2 className="animate-spin" size={20} /> : <><Package size={20} /> نشر المنتج في المتجر</>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddProduct;